import { useCallback } from 'react'
import { useApolloClient } from 'apollo-client'
import { useCachedPromise, useCacheReset } from 'react-cache'
import logger from 'logger'
import { GraphQLError } from 'helpers'
import { useUser } from 'modules/user'
import type { OriginType } from 'typings/graphql'

import couponApplyQuery, { type CouponApplyPayload } from './graph/couponApply.graphql'


export type FreeProduct = CouponApplyPayload['couponApply']['data']['coupon']['freeProduct']

type UseCouponDataProps = {
  couponCode?: string
  origin?: OriginType
}

export type CouponApplyResult = CouponApplyPayload['couponApply']['data']['coupon'] & {
  totalAmount: number
}

const modifyResult = (data: CouponApplyPayload['couponApply']['data']): CouponApplyResult => {
  if (!data) {
    return null
  }

  const { coupon, totalAmount } = data

  return {
    ...coupon,
    totalAmount,
  }
}

export const useResetCouponsCache = () => {
  const reset = useCacheReset()

  return useCallback(() => {
    reset(/COUPON-APPLY-/)
  }, [ reset ])
}

// ATTN this hook is for reading-only
const useCouponData = ({ couponCode: userCouponCode, origin }: UseCouponDataProps = {}) => {
  const { gender } = useUser()

  const apolloClient = useApolloClient()
  const couponCode = userCouponCode?.toUpperCase()

  const { data: couponData, isFetching } = useCachedPromise(async () => {
    try {
      if (!couponCode) {
        return
      }

      const result = await apolloClient.mutate({
        mutation: couponApplyQuery,
        variables: {
          input: {
            couponCode,
            origin,
            validateOnly: true,
          },
          gender,
          validateOnly: true,
        },
        fetchPolicy: 'no-cache',
      })

      if (result.errors) {
        throw new GraphQLError(result.errors)
      }

      const { data } = result.data.couponApply

      return modifyResult(data)
    }
    catch (error) {
      logger.info(error)
    }
  }, `COUPON-APPLY-${couponCode}-${gender}`)

  const isCouponDataFetching = isFetching
  const isCouponUnavailable = !isCouponDataFetching && !couponData

  return {
    couponData,
    isCouponDataFetching,
    isCouponUnavailable,
  }
}


export default useCouponData
